import {
  ActionIcon,
  Anchor,
  Box,
  Center,
  Divider,
  Flex,
  Input,
  Table,
  Text,
  useMatches,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import Popover from "../Popover/Popover";

import { MakeCard } from "@/components/elements";
import WatchlistSelectionBox from "@/components/elements/WatchList/WatchlistSelectionBox";
import { IconBookmark, IconBookmarkFilled, IconPlus, IconX } from "@/components/icons";
import { defaultCurrency } from "@/lib/currency";
import { useCreateWatchlist } from "@/services/watchlists/create-watchlist";
import { formatCurrency, formatPercentage, toPercentFraction } from "@/utils/formatEuro";

import type { ListTableRowProps } from "@/components/elements";
import type { Currency } from "@/types/currency";
import type { MarketCar, MarketCarColumns } from "@/types/market";
import type { Watchlist } from "@/types/watchlist";
import type { FC, ReactNode, SetStateAction } from "react";

export type MarketCarTableRowProps = ListTableRowProps<MarketCar, MarketCarColumns> & {
  setCarToAdd: React.Dispatch<SetStateAction<MarketCar | null>>;
  addCarToWatchlistHandler?: (watchlistIds: string[]) => void;
  removeCarFromWatchlistHandler: (watchlistIdToRemove: string) => void;
  watchlists: Watchlist[] | undefined;
  actionInPending: boolean;
  isWished: boolean;
};

export const MarketCarTableRow: FC<MarketCarTableRowProps> = ({
  checkbox,
  columns,
  data,
  setCarToAdd,
  addCarToWatchlistHandler,
  removeCarFromWatchlistHandler,
  watchlists = [],
  actionInPending,
  isWished,
  selected = false,
  to,
  ...props
}) => {
  const { t } = useTranslation();
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [selectedWatchlists, setSelectedWatchlists] = useState<string[]>([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [isWishedLocal, setIsWishedLocal] = useState(isWished);
  const isMobile = useMatches({ base: true, md: false });

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const createMutation = useCreateWatchlist();

  useEffect(() => {
    setSelectedWatchlists((data.in_wishlists ?? []).map(String));
    setIsWishedLocal((data.in_wishlists?.length ?? 0) > 0);
  }, [data.in_wishlists]);

  const handleCreateNewWatchlist = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!newWatchlistName.trim()) return;
    createMutation.mutate(
      { data: { name: newWatchlistName.trim() } },
      {
        onSuccess: () => {
          setNewWatchlistName("");
          setCreatingNew(false);
        },
      }
    );
  };

  const renderCell = (column: MarketCarColumns) => {
    switch (column) {
      case "model_name":
        return to ? (
          <Anchor
            component={Link}
            // to={paths.app.markets.car.getHref(data.id)}
            to={to}
            size="sm"
            onClick={(e) => e.stopPropagation()}
          >
            {data.make_name} {data[column]}
          </Anchor>
        ) : (
          <Text size="sm">
            {data.make_name} {data[column]}
          </Text>
        );
      case "make_name":
        return (
          <Center>
            <MakeCard make={data[column]} w={30} hideBG />
          </Center>
        );
      case "produced_cars":
        return data[column].toLocaleString();
      case "current_valuation":
        return formatCurrency(data[column], currency.code);
      case "year_over_year_gain_loss": {
        const raw = data[column] as number;
        const isPositive = raw > 0;
        const isNegative = raw < 0;

        return (
          <Text fz="sm" c={isPositive ? "success" : isNegative ? "danger" : undefined}>
            {isPositive ? "+" : ""}
            {formatPercentage(toPercentFraction(raw), undefined, 2)}
          </Text>
        );
      }
      default:
        return data[column] as ReactNode;
    }
  };

  return (
    <Table.Tr
      bg={selected ? "var(--mantine-color-primary-light)" : undefined}
      style={{ cursor: props?.onClick ? "pointer" : "default" }}
      {...props}
    >
      {checkbox && (
        <Table.Td onClick={(evt) => evt.stopPropagation()} w={1}>
          {checkbox}
        </Table.Td>
      )}
      <Table.Td>
        <Popover
          actionInPending={actionInPending}
          popoverOpened={popoverOpened}
          title={t("components.WatchList.add_to_watchlists")}
          setPopoverOpened={setPopoverOpened}
          target={
            <ActionIcon
              variant="light"
              size="md"
              type="button"
              onClick={() => {
                setSelectedWatchlists((data.in_wishlists ?? []).map(String));
                setCarToAdd(data);
                setPopoverOpened((prev) => !prev);
              }}
            >
              {isWishedLocal ? <IconBookmarkFilled /> : <IconBookmark />}
            </ActionIcon>
          }
        >
          {watchlists.length > 0 ? (
            <>
              {!isMobile && (
                <Text mt="sm" mb="lg">
                  {t("components.WatchList.add_to_watchlists")}
                </Text>
              )}
              <WatchlistSelectionBox
                carToAdd={data}
                selectedWatchlists={selectedWatchlists}
                setSelectedWatchlists={setSelectedWatchlists}
                addCarToWatchlistHandler={(ids) => {
                  setIsWishedLocal(true);
                  addCarToWatchlistHandler?.(ids);
                }}
                removeCarFromWatchlistHandler={(wid) => {
                  removeCarFromWatchlistHandler(wid);
                  setSelectedWatchlists((prev) => {
                    const updated = prev.filter((id) => id !== wid);
                    setIsWishedLocal(updated.length > 0);
                    return updated;
                  });
                }}
                watchlists={watchlists}
              />
            </>
          ) : (
            <Text mt="sm" mb="sm">
              {t("components.WatchList.no_watchlists_available")}
            </Text>
          )}
          <Box mt="lg">
            {watchlists.length > 0 && <Divider mb="lg" />}

            {creatingNew ? (
              <Flex gap="md" justify="space-between" align="center">
                <Input
                  value={newWatchlistName}
                  onChange={(e) => setNewWatchlistName(e.currentTarget.value)}
                  placeholder={t("components.WatchList.your_watchlist_name")}
                  size="xs"
                  autoFocus
                  w="200px"
                />
                <Flex gap="md">
                  <ActionIcon
                    variant="light"
                    color="green"
                    size="sm"
                    disabled={!newWatchlistName.trim()}
                    onClick={(e) => handleCreateNewWatchlist(e)}
                  >
                    <IconPlus size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="red"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreatingNew(false);
                      setNewWatchlistName("");
                    }}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Flex>
              </Flex>
            ) : (
              <Flex justify="space-between" align="center">
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreatingNew(true);
                  }}
                >
                  <Anchor fz="sm">{t("components.WatchList.create_new_watchlist")}</Anchor>
                </Box>
                <ActionIcon
                  variant="light"
                  size="sm"
                  onClick={() => {
                    setCreatingNew(true);
                  }}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Flex>
            )}
          </Box>
        </Popover>
      </Table.Td>

      {columns.map((column) => (
        <Table.Td key={column}>{renderCell(column)}</Table.Td>
      ))}
    </Table.Tr>
  );
};
